import React, { useState, useEffect } from 'react'
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
  useTheme,
  Badge,
  Tooltip,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Popover,
  Collapse,
} from '@mui/material'
import {
  Menu as MenuIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  Notifications as NotificationsIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
  Close as CloseIcon,
  DeleteSweep as DeleteSweepIcon,
  BugReport as BugReportIcon,
} from '@mui/icons-material'
import {
  Assignment as AssignmentIcon,
  Schedule as ScheduleIcon,
  Assessment as AssessmentIcon,
  AdminPanelSettings as AdminIcon,
  Payment as PaymentIcon,
  Category as CategoryIcon,
  Settings as SettingsIcon,
  MedicalServices as MedicalServicesIcon,
  Engineering as EngineeringIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material'
import { useAuth } from '../../contexts/AuthContext'
import { useNavigate, useLocation } from 'react-router-dom'
import { useThemeMode } from '../../contexts/ThemeContext'
import { useAppSettings } from '../../contexts/AppSettingsContext'
import { usePermission } from '../../hooks/usePermission'
import apiClient from '../../services/api'
import toast from 'react-hot-toast'

interface NavbarProps {
  onMenuClick: () => void
}

interface Notification {
  id: string
  title: string
  message: string
  type: string
  read_at: string | null
  created_at: string
}

interface MenuItem {
  text: string
  icon: React.ReactNode
  path: string
  /** Permisiuni necesare pentru a vedea acest meniu (oricare din listă) */
  permissions: string[]
}

// Submeniuri pentru Programator
const getProgramatorSubmenu = (): MenuItem[] => [
    {
      text: 'Gărzi',
      icon: <MedicalServicesIcon sx={{ fontSize: '1rem' }} />,
      path: '/programator/garzi',
      permissions: ['shifts:view'],
    },
    {
      text: 'Ture',
      icon: <EngineeringIcon sx={{ fontSize: '1rem' }} />,
      path: '/programator/ture',
      permissions: ['shifts:view'],
    },
  ]

// Meniul de navigare bazat pe permisiuni (comun pentru desktop și mobile)
// Note: Pacienți și Documente au fost eliminate - funcționalitățile sunt în Worklist
const getMenuItems = (): MenuItem[] => [
    {
      text: 'Worklist',
      icon: <AssignmentIcon />,
      path: '/',
      permissions: ['studies:view'],
    },
    {
      text: 'Analytics',
      icon: <AssessmentIcon />,
      path: '/analytics',
      permissions: ['analytics:view'],
    },
    {
      text: 'Billing',
      icon: <PaymentIcon />,
      path: '/billing',
      permissions: ['billing:view'],
    },
    {
      text: 'Body Parts',
      icon: <CategoryIcon />,
      path: '/body-parts',
      permissions: ['bodyparts:view', 'bodyparts:edit'],
    },
    {
      text: 'Configurare',
      icon: <SettingsIcon />,
      path: '/configurare',
      permissions: ['users:view'],
    },
    {
      text: 'Administrare',
      icon: <AdminIcon />,
      path: '/admin',
      permissions: ['users:view'],
    },
  ]

// Componenta pentru menu items în navbar (desktop)
const NavMenuItems: React.FC = () => {
  const { hasPermission, userRole } = usePermission()
  const navigate = useNavigate()
  const location = useLocation()
  const [programatorAnchor, setProgramatorAnchor] = useState<null | HTMLElement>(null)

  const menuItems = getMenuItems()
  const programatorSubmenu = getProgramatorSubmenu()

  // Verifică dacă utilizatorul poate accesa Programator
  const canAccessProgramator = () => {
    if (userRole === 'admin') return true
    if (userRole === 'operator') return true
    if (userRole === 'tehnician') return true
    if (userRole === 'medic') return true
    return false
  }

  // Filtrează submeniurile Programator bazat pe rol
  const getFilteredProgramatorSubmenu = () => {
    return programatorSubmenu.filter(item => {
      if (userRole === 'admin') return true
      if (userRole === 'operator') return true
      if (userRole === 'tehnician') return item.path === '/programator/ture'
      if (userRole === 'medic') return item.path === '/programator/garzi'
      return false
    })
  }

  const filteredMenuItems = menuItems.filter(item => {
    if (userRole === 'admin') {
      return true
    }
    
    if (userRole === 'operator') {
      const operatorAllowedPaths = ['/', '/billing', '/configurare']
      return operatorAllowedPaths.includes(item.path)
    }
    
    if (item.permissions.length === 0) {
      return true
    }
    
    if (userRole === 'tehnician') {
      const restrictedPaths = ['/billing', '/body-parts', '/admin', '/configurare', '/analytics']
      if (restrictedPaths.includes(item.path)) {
        return false
      }
      if (item.path === '/') {
        return true
      }
    }
    
    try {
      const hasAccess = hasPermission(item.permissions)
      return hasAccess
    } catch (error) {
      console.error('Error checking permissions:', error)
      return false
    }
  })

  const handleItemClick = (item: MenuItem) => {
    navigate(item.path)
  }

  const handleProgramatorClick = (event: React.MouseEvent<HTMLElement>) => {
    setProgramatorAnchor(event.currentTarget)
  }

  const handleProgramatorClose = () => {
    setProgramatorAnchor(null)
  }

  const handleSubmenuClick = (item: MenuItem) => {
    navigate(item.path)
    handleProgramatorClose()
  }

  const isItemActive = (item: MenuItem): boolean => {
    if (item.path === '/') {
      return location.pathname === '/'
    }
    return location.pathname.startsWith(item.path)
  }

  const isProgramatorActive = (): boolean => {
    return location.pathname.startsWith('/programator')
  }

  const filteredProgramatorSubmenu = getFilteredProgramatorSubmenu()

  return (
    <>
      {/* Worklist - primul item */}
      {filteredMenuItems.filter(i => i.path === '/').map((item) => {
        const isActive = isItemActive(item)
        return (
          <Button
            key={item.text}
            onClick={() => handleItemClick(item)}
            startIcon={item.icon}
            variant={isActive ? 'contained' : 'text'}
            size="small"
            sx={{
              color: isActive ? 'primary.contrastText' : 'text.primary',
              backgroundColor: isActive ? 'primary.main' : 'transparent',
              fontWeight: isActive ? 600 : 400,
              '&:hover': {
                backgroundColor: isActive ? 'primary.dark' : 'action.hover',
              },
            }}
          >
            {item.text}
          </Button>
        )
      })}

      {/* Programator cu dropdown */}
      {canAccessProgramator() && filteredProgramatorSubmenu.length > 0 && (
        <>
          <Button
            onClick={handleProgramatorClick}
            startIcon={<ScheduleIcon />}
            endIcon={<ExpandMoreIcon sx={{ fontSize: '1rem !important', ml: -0.5 }} />}
            variant={isProgramatorActive() ? 'contained' : 'text'}
            size="small"
            sx={{
              color: isProgramatorActive() ? 'primary.contrastText' : 'text.primary',
              backgroundColor: isProgramatorActive() ? 'primary.main' : 'transparent',
              fontWeight: isProgramatorActive() ? 600 : 400,
              '&:hover': {
                backgroundColor: isProgramatorActive() ? 'primary.dark' : 'action.hover',
              },
            }}
          >
            Programator
          </Button>
          <Popover
            open={Boolean(programatorAnchor)}
            anchorEl={programatorAnchor}
            onClose={handleProgramatorClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            sx={{
              '& .MuiPopover-paper': {
                mt: 0.5,
                bgcolor: 'background.paper',
                boxShadow: 4,
                borderRadius: 1,
                minWidth: 140,
              }
            }}
          >
            <List dense disablePadding sx={{ py: 0.5 }}>
              {filteredProgramatorSubmenu.map((item) => {
                const isSubActive = isItemActive(item)
                return (
                  <ListItem key={item.text} disablePadding>
                    <ListItemButton
                      onClick={() => handleSubmenuClick(item)}
                      sx={{
                        py: 0.75,
                        px: 1.5,
                        backgroundColor: isSubActive ? 'primary.main' : 'transparent',
                        color: isSubActive ? 'primary.contrastText' : 'text.primary',
                        '&:hover': {
                          backgroundColor: isSubActive ? 'primary.dark' : 'action.hover',
                        },
                        '& .MuiListItemIcon-root': {
                          color: isSubActive ? 'primary.contrastText' : 'text.primary',
                          minWidth: 28,
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText 
                        primary={item.text} 
                        primaryTypographyProps={{ 
                          fontSize: '0.8rem',
                          fontWeight: isSubActive ? 600 : 400,
                        }} 
                      />
                    </ListItemButton>
                  </ListItem>
                )
              })}
            </List>
          </Popover>
        </>
      )}

      {/* Restul itemurilor (fără Worklist) */}
      {filteredMenuItems.filter(i => i.path !== '/').map((item) => {
        const isActive = isItemActive(item)
        
        return (
          <Button
            key={item.text}
            onClick={() => handleItemClick(item)}
            startIcon={item.icon}
            variant={isActive ? 'contained' : 'text'}
            size="small"
            sx={{
              color: isActive ? 'primary.contrastText' : 'text.primary',
              backgroundColor: isActive ? 'primary.main' : 'transparent',
              fontWeight: isActive ? 600 : 400,
              '&:hover': {
                backgroundColor: isActive ? 'primary.dark' : 'action.hover',
              },
            }}
          >
            {item.text}
          </Button>
        )
      })}
    </>
  )
}

const Navbar: React.FC<NavbarProps> = ({ onMenuClick: _onMenuClick }) => {
  const theme = useTheme()
  const { user, logout } = useAuth()
  const { toggleThemeMode, isDarkMode } = useThemeMode()
  const { settings } = useAppSettings()
  const navigate = useNavigate()
  const location = useLocation()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [notificationAnchorEl, setNotificationAnchorEl] = useState<null | HTMLElement>(null)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [loadingNotifications, setLoadingNotifications] = useState(false)
  const [feedbackOpen, setFeedbackOpen] = useState(false)
  const [feedbackMessage, setFeedbackMessage] = useState('')
  const [sendingFeedback, setSendingFeedback] = useState(false)

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleProfileMenuClose = () => {
    setAnchorEl(null)
  }

  const handleNotificationMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchorEl(event.currentTarget)
  }

  const handleNotificationMenuClose = () => {
    setNotificationAnchorEl(null)
  }

  const handleMobileMenuOpen = () => {
    setMobileMenuOpen(true)
  }

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false)
  }

  // Obține menu items filtrate pentru utilizatorul curent (bazat pe permisiuni)
  const { hasPermission: checkPermission, userRole: currentUserRole } = usePermission()
  const menuItems = getMenuItems()
  const programatorSubmenu = getProgramatorSubmenu()
  const [mobileProgExpanded, setMobileProgExpanded] = useState(false)

  // Verifică dacă utilizatorul poate accesa Programator (pentru mobile menu)
  const canAccessProgramatorMobile = () => {
    if (currentUserRole === 'admin') return true
    if (currentUserRole === 'operator') return true
    if (currentUserRole === 'tehnician') return true
    if (currentUserRole === 'medic') return true
    return false
  }

  // Filtrează submeniurile Programator bazat pe rol (pentru mobile)
  const getFilteredProgramatorSubmenuMobile = () => {
    return programatorSubmenu.filter(item => {
      if (currentUserRole === 'admin') return true
      if (currentUserRole === 'operator') return true
      if (currentUserRole === 'tehnician') return item.path === '/programator/ture'
      if (currentUserRole === 'medic') return item.path === '/programator/garzi'
      return false
    })
  }

  const filteredMenuItems = menuItems.filter(item => {
    if (currentUserRole === 'admin') {
      return true
    }
    
    if (currentUserRole === 'operator') {
      const operatorAllowedPaths = ['/', '/billing', '/configurare']
      return operatorAllowedPaths.includes(item.path)
    }
    
    if (item.permissions.length === 0) {
      return true
    }
    
    if (currentUserRole === 'tehnician') {
      const restrictedPaths = ['/analytics', '/billing', '/body-parts', '/admin', '/configurare']
      if (restrictedPaths.includes(item.path)) {
        return false
      }
      if (item.path === '/') {
        return true
      }
    }
    
    return checkPermission(item.permissions)
  })

  const filteredProgramatorSubmenuMobile = getFilteredProgramatorSubmenuMobile()

  const handleMenuItemClick = (path: string) => {
    navigate(path)
    handleMobileMenuClose()
  }

  const isItemActive = (path: string): boolean => {
    if (path === '/') {
      return location.pathname === '/'
    }
    return location.pathname.startsWith(path)
  }

  const isProgramatorActiveMobile = (): boolean => {
    return location.pathname.startsWith('/programator')
  }

  // Încarcă notificările (doar cele necitite pentru a evita acumularea)
  const loadNotifications = async () => {
    try {
      setLoadingNotifications(true)
      // Încarcă doar notificările necitite pentru a evita acumularea în UI
      const response = await apiClient.get('/notifications/?page=1&size=10&unread=true')
      setNotifications(response.data.notifications || [])

      // IMPORTANT: lista e limitată la size=10, dar contorul trebuie să reflecte totalul real
      // Backend returnează `total` pentru query-ul curent (unread=true), deci este numărul de necitite.
      const totalUnread = typeof response.data.total === 'number'
        ? response.data.total
        : (response.data.notifications?.filter((n: Notification) => !n.read_at).length || 0)
      setUnreadCount(totalUnread)
    } catch (error) {
      console.error('Error loading notifications:', error)
      // În caz de eroare, păstrează notificările hardcodate pentru fallback
      setNotifications([])
      setUnreadCount(0)
    } finally {
      setLoadingNotifications(false)
    }
  }

  // Marchează o notificare ca citită (acknowledge)
  const markAsRead = async (notificationId: string) => {
    try {
      await apiClient.patch(`/notifications/${notificationId}/read`)
      // Reîncarcă din server ca să fie consistent (și să nu reapară după relogin)
      await loadNotifications()
    } catch (error) {
      console.error('Error marking notification as read:', error)
      // În caz de eroare, reîncarcă lista pentru a sincroniza starea
      loadNotifications()
    }
  }

  // Șterge o notificare complet
  const deleteNotification = async (notificationId: string) => {
    try {
      await apiClient.delete(`/notifications/${notificationId}`)
      await loadNotifications()
    } catch (error) {
      console.error('Error deleting notification:', error)
    }
  }

  // Șterge toate notificările utilizatorului
  const deleteAllNotifications = async () => {
    const toastId = 'notifications-delete-all'
    toast.loading('Se șterg notificările...', { id: toastId })
    try {
      const response = await apiClient.delete('/notifications/all')
      await loadNotifications()
      handleNotificationMenuClose()
      const deleted = response?.data?.deleted
      toast.success(
        typeof deleted === 'number' ? `Șterse ${deleted} notificări` : 'Notificări șterse',
        { id: toastId }
      )
    } catch (error) {
      console.error('Error deleting all notifications:', error)
      toast.error('Eroare la ștergerea notificărilor', { id: toastId })
      await loadNotifications()
    }
  }

  // Încarcă notificările la montarea componentei
  useEffect(() => {
    if (user) {
      loadNotifications()
    }
  }, [user])


  const handleLogout = async () => {
    handleProfileMenuClose()
    await logout()
    navigate('/login')
  }

  const handleOpenFeedback = () => {
    setFeedbackOpen(true)
  }

  const handleCloseFeedback = () => {
    if (sendingFeedback) return
    setFeedbackOpen(false)
    setFeedbackMessage('')
  }

  const handleSendFeedback = async () => {
    const message = feedbackMessage.trim()
    if (!message) return

    setSendingFeedback(true)
    const toastId = 'feedback-send'
    toast.loading('Se trimite mesajul...', { id: toastId })

    try {
      const page = `${location.pathname}${location.search}${location.hash}`
      const pageTitle = document?.title || ''
      const clientTime = new Date().toISOString()

      await apiClient.post('/feedback', {
        message,
        page,
        page_title: pageTitle,
        client_time: clientTime,
      })

      toast.success('Mulțumim! Mesaj trimis.', { id: toastId })
      handleCloseFeedback()
    } catch (error: any) {
      console.error('Error sending feedback:', error)
      const detail = error?.response?.data?.detail
      toast.error(detail || 'Eroare la trimiterea mesajului', { id: toastId })
    } finally {
      setSendingFeedback(false)
    }
  }



  return (
    <AppBar
      position="fixed"
      sx={{
        width: '100%',
        zIndex: theme.zIndex.drawer + 1,
        backgroundColor: 'background.paper',
        color: 'text.primary',
        boxShadow: 1,
      }}
    >
      <Toolbar
        variant="dense"
        sx={{
          minHeight: 33,
          height: 33,
          px: 0.75,
          '@media (min-width:600px)': {
            minHeight: 33,
            height: 33,
          },
          '& .MuiIconButton-root': {
            padding: '2px',
          },
          overflow: 'hidden',
        }}
      >
        {/* Menu button pentru mobile */}
        <IconButton
          color="inherit"
          aria-label="open drawer"
          edge="start"
          onClick={handleMobileMenuOpen}
          size="small"
          sx={{ mr: 0.5, display: { md: 'none' } }}
        >
          <MenuIcon fontSize="small" />
        </IconButton>

        {/* App logo and menu */}
        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: { xs: 1, md: 2 } }}>
          <Box
            component="img"
            src="/logo.svg"
            alt={settings?.app_name || 'ZenRIS'}
            sx={{
              height: { xs: 20, sm: 24 },
              cursor: 'pointer',
              flexShrink: 0,
            }}
            onClick={() => navigate('/')}
          />
          
          {/* Navigation Menu - Desktop only */}
          <Box sx={{ 
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center', 
            gap: 1,
            overflow: 'hidden',
            '& .MuiButton-root': {
              minWidth: 'auto',
              whiteSpace: 'nowrap',
              textTransform: 'none',
              fontSize: '0.72rem',
              fontWeight: 400,
              px: 1.25,
              py: 0,
              minHeight: 18,
              borderRadius: 1,
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }
          }}>
            {/* Menu items will be inserted here */}
            <NavMenuItems />
          </Box>
        </Box>


        {/* Right side actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
          {/* Feedback / Bug report */}
          <Tooltip title="Raportează problemă / sugestie">
            <IconButton
              color="inherit"
              onClick={handleOpenFeedback}
              size="small"
              sx={{ ml: { xs: 0, sm: 0.25 } }}
            >
              <BugReportIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>

          {/* Dark mode toggle */}
          <Tooltip title={isDarkMode ? 'Mod luminos' : 'Mod întunecat'}>
            <IconButton
              color="inherit"
              onClick={toggleThemeMode}
              size="small"
              sx={{ ml: { xs: 0, sm: 0.25 } }}
            >
              {isDarkMode ? <LightModeIcon sx={{ fontSize: 18 }} /> : <DarkModeIcon sx={{ fontSize: 18 }} />}
            </IconButton>
          </Tooltip>

          {/* Notifications */}
          <Tooltip title="Notificări">
            <IconButton
              color="inherit"
              onClick={handleNotificationMenuOpen}
              size="small"
              sx={{ ml: { xs: 0, sm: 0.25 } }}
            >
              <Badge
                badgeContent={unreadCount > 0 ? unreadCount : undefined}
                color="error"
                sx={{ '& .MuiBadge-badge': { fontSize: '0.7rem', height: 16, minWidth: 16 } }}
              >
                <NotificationsIcon sx={{ fontSize: 18 }} />
              </Badge>
            </IconButton>
          </Tooltip>

          {/* User menu */}
          <Tooltip title="Meniu utilizator">
            <IconButton
              size="small"
              edge="end"
              aria-label="account of current user"
              aria-controls="primary-search-account-menu"
              aria-haspopup="true"
              onClick={handleProfileMenuOpen}
              color="inherit"
              sx={{ ml: { xs: 0, sm: 0.25 } }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: { xs: 0.5, sm: 1 },
                  px: { xs: 0.25, sm: 0.5 },
                  py: 0,
                  borderRadius: 1,
                  bgcolor: 'transparent',
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
                <Avatar 
                  sx={{ 
                    width: { xs: 18, sm: 21 }, 
                    height: { xs: 18, sm: 21 }, 
                    bgcolor: 'primary.main',
                    fontSize: { xs: '0.6rem', sm: '0.65rem' },
                  }}
                >
                  {user?.first_name && user?.last_name 
                    ? `${user.first_name[0]}${user.last_name[0]}`
                    : user?.email?.[0]?.toUpperCase() || 'U'
                  }
                </Avatar>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    color: 'text.primary',
                    display: 'none',
                    fontSize: '0.72rem',
                  }}
                >
                  {user?.first_name && user?.last_name 
                    ? `${user.first_name} ${user.last_name}`
                    : user?.email
                  }
                </Typography>
              </Box>
            </IconButton>
          </Tooltip>
        </Box>
      </Toolbar>

      {/* User menu dropdown */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        onClick={handleProfileMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 200,
          },
        }}
      >
        <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {user?.first_name && user?.last_name 
              ? `${user.first_name} ${user.last_name}`
              : user?.email
            }
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {user?.role === 'admin' && 'Administrator'}
            {user?.role === 'operator' && 'Operator'}
            {user?.role === 'medic' && 'Medic'}
            {user?.role === 'tehnician' && 'Tehnician'}
          </Typography>
        </Box>
        
        <MenuItem onClick={() => navigate('/profile')}>
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          Profil
        </MenuItem>
        
        <Divider />
        
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          Deconectare
        </MenuItem>
      </Menu>

      {/* Feedback dialog */}
      <Dialog open={feedbackOpen} onClose={handleCloseFeedback} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BugReportIcon sx={{ fontSize: 18 }} />
            Trimite feedback
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Spune-ne ce nu funcționează sau ce ai vrea îmbunătățit. Se vor include automat pagina curentă, utilizatorul și ora.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={6}
            label="Mesaj"
            placeholder="Ex: În pagina Pacienți, filtrul X nu se aplică dacă..."
            value={feedbackMessage}
            onChange={(e) => setFeedbackMessage(e.target.value)}
            disabled={sendingFeedback}
            sx={{
              '& .MuiOutlinedInput-root': {
                height: 'auto',
                alignItems: 'flex-start',
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseFeedback} disabled={sendingFeedback}>
            Renunță
          </Button>
          <Button
            variant="contained"
            onClick={handleSendFeedback}
            disabled={sendingFeedback || feedbackMessage.trim().length === 0}
            startIcon={sendingFeedback ? <CircularProgress size={18} /> : undefined}
          >
            Trimite
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notifications menu */}
      <Menu
        anchorEl={notificationAnchorEl}
        open={Boolean(notificationAnchorEl)}
        onClose={handleNotificationMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 300,
            maxHeight: 400,
          },
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1,
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Notificări
          </Typography>
          <Button
            size="small"
            color="error"
            variant="text"
            startIcon={<DeleteSweepIcon fontSize="small" />}
            disabled={unreadCount === 0 && notifications.length === 0}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              deleteAllNotifications()
            }}
            sx={{ minWidth: 'auto', px: 1, py: 0.25, fontSize: '0.75rem', textTransform: 'none' }}
          >
            Șterge tot
          </Button>
        </Box>
        
        {loadingNotifications ? (
          <MenuItem>
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Se încarcă...
              </Typography>
            </Box>
          </MenuItem>
        ) : notifications.length > 0 ? (
          notifications.map((notification) => (
            <MenuItem 
              key={notification.id}
              onClick={() => {
                // Marchează ca citită când se face click pe notificare (doar dacă nu e deja citită)
                if (!notification.read_at) {
                  markAsRead(notification.id)
                }
              }}
              sx={{ 
                bgcolor: notification.read_at ? 'transparent' : 'action.hover',
                '&:hover': { bgcolor: 'action.selected' },
                position: 'relative',
                pr: 6 // Spațiu pentru butonul X
              }}
            >
              <Box sx={{ width: '100%' }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: notification.read_at ? 'normal' : 'bold',
                    mb: 0.5 
                  }}
                >
                  {notification.title}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  {notification.message}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {new Date(notification.created_at).toLocaleString('ro-RO')}
                </Typography>
              </Box>
              
              {/* Buton X pentru ștergere */}
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation() // Previne declanșarea click-ului pe MenuItem
                  deleteNotification(notification.id)
                }}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  opacity: 0.6,
                  '&:hover': { opacity: 1, bgcolor: 'action.hover' }
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </MenuItem>
          ))
        ) : (
          <MenuItem>
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Nu există notificări
              </Typography>
            </Box>
          </MenuItem>
        )}
      </Menu>

      {/* Mobile Menu Drawer */}
      <Drawer
        anchor="left"
        open={mobileMenuOpen}
        onClose={handleMobileMenuClose}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 280,
            pt: 8, // Spațiu pentru navbar
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Box sx={{ width: 280, height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <Box
            sx={{
              px: 2,
              py: 2,
              borderBottom: 1,
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexShrink: 0,
            }}
          >
            <Box
              component="img"
              src="/logo.svg"
              alt={settings?.app_name || 'ZenRIS'}
              sx={{ height: 28 }}
            />
            <IconButton
              onClick={handleMobileMenuClose}
              size="small"
              sx={{ ml: 'auto' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Menu Items - Scrollable */}
          <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
            <List sx={{ pt: 1 }}>
              {/* Worklist - primul item */}
              {filteredMenuItems.filter(i => i.path === '/').map((item) => {
                const isActive = isItemActive(item.path)
                return (
                  <ListItem key={item.path} disablePadding>
                    <ListItemButton
                      onClick={() => handleMenuItemClick(item.path)}
                      selected={isActive}
                      sx={{
                        py: 1.5,
                        px: 2,
                        '&.Mui-selected': {
                          backgroundColor: 'primary.main',
                          color: 'primary.contrastText',
                          '&:hover': { backgroundColor: 'primary.dark' },
                          '& .MuiListItemIcon-root': { color: 'primary.contrastText' },
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40, color: isActive ? 'primary.contrastText' : 'text.primary' }}>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText primary={item.text} primaryTypographyProps={{ fontWeight: isActive ? 600 : 400 }} />
                    </ListItemButton>
                  </ListItem>
                )
              })}

              {/* Programator cu submeniuri expandabile */}
              {canAccessProgramatorMobile() && filteredProgramatorSubmenuMobile.length > 0 && (
                <>
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => setMobileProgExpanded(!mobileProgExpanded)}
                      selected={isProgramatorActiveMobile()}
                      sx={{
                        py: 1.5,
                        px: 2,
                        '&.Mui-selected': {
                          backgroundColor: 'primary.main',
                          color: 'primary.contrastText',
                          '&:hover': { backgroundColor: 'primary.dark' },
                          '& .MuiListItemIcon-root': { color: 'primary.contrastText' },
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40, color: isProgramatorActiveMobile() ? 'primary.contrastText' : 'text.primary' }}>
                        <ScheduleIcon />
                      </ListItemIcon>
                      <ListItemText primary="Programator" primaryTypographyProps={{ fontWeight: isProgramatorActiveMobile() ? 600 : 400 }} />
                      <ExpandMoreIcon sx={{ 
                        transform: mobileProgExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s',
                        color: isProgramatorActiveMobile() ? 'primary.contrastText' : 'text.secondary',
                      }} />
                    </ListItemButton>
                  </ListItem>
                  <Collapse in={mobileProgExpanded} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {filteredProgramatorSubmenuMobile.map((subItem) => {
                        const isSubActive = isItemActive(subItem.path)
                        return (
                          <ListItem key={subItem.path} disablePadding>
                            <ListItemButton
                              onClick={() => handleMenuItemClick(subItem.path)}
                              selected={isSubActive}
                              sx={{
                                py: 1,
                                pl: 4,
                                pr: 2,
                                '&.Mui-selected': {
                                  backgroundColor: 'primary.light',
                                  color: 'primary.contrastText',
                                  '&:hover': { backgroundColor: 'primary.main' },
                                  '& .MuiListItemIcon-root': { color: 'primary.contrastText' },
                                },
                              }}
                            >
                              <ListItemIcon sx={{ minWidth: 32, color: isSubActive ? 'primary.contrastText' : 'text.primary' }}>
                                {subItem.icon}
                              </ListItemIcon>
                              <ListItemText primary={subItem.text} primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: isSubActive ? 600 : 400 }} />
                            </ListItemButton>
                          </ListItem>
                        )
                      })}
                    </List>
                  </Collapse>
                </>
              )}

              {/* Restul itemurilor (fără Worklist) */}
              {filteredMenuItems.filter(i => i.path !== '/').map((item) => {
                const isActive = isItemActive(item.path)
                return (
                  <ListItem key={item.path} disablePadding>
                    <ListItemButton
                      onClick={() => handleMenuItemClick(item.path)}
                      selected={isActive}
                      sx={{
                        py: 1.5,
                        px: 2,
                        '&.Mui-selected': {
                          backgroundColor: 'primary.main',
                          color: 'primary.contrastText',
                          '&:hover': { backgroundColor: 'primary.dark' },
                          '& .MuiListItemIcon-root': { color: 'primary.contrastText' },
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40, color: isActive ? 'primary.contrastText' : 'text.primary' }}>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText primary={item.text} primaryTypographyProps={{ fontWeight: isActive ? 600 : 400 }} />
                    </ListItemButton>
                  </ListItem>
                )
              })}
            </List>
          </Box>

          {/* User Info și Actions - Fixed at bottom */}
          <Box
            sx={{
              borderTop: 1,
              borderColor: 'divider',
              flexShrink: 0,
              pt: 2,
            }}
          >
            <Box sx={{ px: 2, py: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: 'primary.main',
                    fontSize: '0.75rem',
                  }}
                >
                  {user?.first_name && user?.last_name
                    ? `${user.first_name[0]}${user.last_name[0]}`
                    : user?.email?.[0]?.toUpperCase() || 'U'}
                </Avatar>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {user?.first_name && user?.last_name
                      ? `${user.first_name} ${user.last_name}`
                      : user?.email}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {user?.role === 'admin' && 'Administrator'}
                    {user?.role === 'operator' && 'Operator'}
                    {user?.role === 'medic' && 'Medic'}
                    {user?.role === 'tehnician' && 'Tehnician'}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Divider />

            <List>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => {
                    handleMobileMenuClose()
                    navigate('/profile')
                  }}
                  sx={{ py: 1.5, px: 2 }}
                >
                  <ListItemIcon>
                    <PersonIcon />
                  </ListItemIcon>
                  <ListItemText primary="Profil" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={async () => {
                    handleMobileMenuClose()
                    await handleLogout()
                  }}
                  sx={{ py: 1.5, px: 2 }}
                >
                  <ListItemIcon>
                    <LogoutIcon />
                  </ListItemIcon>
                  <ListItemText primary="Deconectare" />
                </ListItemButton>
              </ListItem>
            </List>
          </Box>
        </Box>
      </Drawer>
    </AppBar>
  )
}

export default Navbar
